// client/src/components/secure/SiteSearchPanel.js — a client's space search.
//
// Sits on the client record, because that is where the work belongs: one client,
// one engagement, one list of buildings. The main portal stays clean.
//
// The table is the rolodex format the owner asked for — address, size, zoning,
// the district's rule, the village official, the listing agent — with a release
// switch, because nothing reaches the client portal until a licensee says so.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import ZoningBrief from './ZoningBrief';
import ZoningLookupBox from './ZoningLookupBox';
import Modal from './Modal';
import AddListingPaste from './AddListingPaste';
import DataList from '../shared/DataList';
import { BusinessPicker, NotResearched } from './BusinessPicker';
import LookupLinks, { AddressLink, LinkNote } from '../shared/LookupLinks';
import { SOURCE_NAME } from '../../utils/listingLinks';

const USE_TONE = {
    by_right: 'bg-green-100 text-green-800 border-green-200',
    special_use: 'bg-amber-100 text-amber-800 border-amber-200',
    not_allowed: 'bg-red-100 text-red-800 border-red-200',
    unverified: 'bg-gray-100 text-gray-700 border-gray-200',
};
const USE_LABEL = {
    by_right: 'BY RIGHT', special_use: 'SPECIAL USE', not_allowed: 'NOT ALLOWED', unverified: 'UNVERIFIED',
};
const STATUS_TONE = {
    new: 'bg-blue-50 text-blue-700', shortlisted: 'bg-green-50 text-green-700',
    toured: 'bg-indigo-50 text-indigo-700', offer: 'bg-purple-50 text-purple-700',
    rejected: 'bg-gray-100 text-gray-500', closed: 'bg-gray-100 text-gray-500',
    off_market: 'bg-stone-200 text-stone-600',
};

const CAND_STATUSES = ['new', 'shortlisted', 'toured', 'offer', 'rejected', 'off_market', 'closed'];
// Off the market and rejected are history, not the live list: hidden unless asked for.
const PARKED = ['rejected', 'off_market', 'closed'];
const STATUS_LABEL = { off_market: 'off market' };
const CLOSE_REASONS = [
    ['leased', 'Leased — client signed'],
    ['purchased', 'Purchased — client bought'],
    ['withdrawn', 'Withdrawn — client stopped'],
    ['expired', 'Expired — engagement ended'],
];


// "New" means: the sweep found it recently AND nobody has triaged it yet. Once a
// licensee moves it out of 'new', the badge goes, even if it arrived this morning.
const FRESH_DAYS = 3;
const isFresh = (c) => {
    if (c.status !== 'new' || !c.first_seen_at) return false;
    const age = (Date.now() - new Date(c.first_seen_at).getTime()) / 86400000;
    return age <= FRESH_DAYS;
};

const Tag = ({ tone, children }) => (
    <span className={`inline-block whitespace-nowrap rounded border px-2 py-0.5 text-[10px] font-semibold ${tone}`}>{children}</span>
);

const NewSearchForm = ({ clientId, onDone, onCancel }) => {
    const [form, setForm] = useState({
        title: '', use_category: '', intent: 'lease', towns: '', min_sf: '', max_sf: '',
        max_rate: '', must_haves: '', notes: '', rep_agreement_on_file: false, sweep_frequency: 'weekly',
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const apiClient = useApiClient();
    const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true); setError('');
        try {
            await apiClient.post('/api/site-searches', {
                client_id: clientId,
                title: form.title,
                use_category: form.use_category,
                intent: form.intent,
                rep_agreement_on_file: form.rep_agreement_on_file,
                sweep_frequency: form.sweep_frequency,
                criteria: {
                    towns: form.towns.split(',').map((t) => t.trim()).filter(Boolean),
                    min_sf: form.min_sf ? Number(form.min_sf) : null,
                    max_sf: form.max_sf ? Number(form.max_sf) : null,
                    max_rate: form.max_rate || null,
                    must_haves: form.must_haves.split(',').map((t) => t.trim()).filter(Boolean),
                    notes: form.notes || null,
                },
            });
            onDone();
        } catch (err) {
            setError(err.message || 'Could not start the search.');
        } finally {
            setSaving(false);
        }
    };

    const field = 'mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm';
    const label = 'block text-xs font-semibold uppercase tracking-wide text-gray-600';

    return (
        <form onSubmit={submit} className="space-y-3">
            {error && <p className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
            <div>
                <label className={label}>Title</label>
                <input required className={field} value={form.title} onChange={set('title')}
                       placeholder="Cosmetology school — north suburbs" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                    <label className={label}>Business (use category)</label>
                    <div className="mt-1"><BusinessPicker value={form.use_category} onChange={(v) => setForm({ ...form, use_category: v })} required /></div>
                    <p className="mt-1 text-[11px] text-gray-500">From the Business Library — the Zoning Library is keyed on it. Any business can be picked; towns not researched for it yet can be sent for research.</p>
                </div>
                <div>
                    <label className={label}>Lease or purchase</label>
                    <select className={field} value={form.intent} onChange={set('intent')}>
                        <option value="lease">Lease</option>
                        <option value="purchase">Purchase</option>
                        <option value="either">Either</option>
                    </select>
                </div>
            </div>
            <div>
                <label className={label}>Towns (comma separated)</label>
                <input className={field} value={form.towns} onChange={set('towns')}
                       placeholder="Northbrook, Lincolnshire, Vernon Hills, Palatine" />
            </div>
            <div className="grid grid-cols-3 gap-3">
                <div><label className={label}>Min SF</label><input className={field} value={form.min_sf} onChange={set('min_sf')} /></div>
                <div><label className={label}>Max SF</label><input className={field} value={form.max_sf} onChange={set('max_sf')} /></div>
                <div><label className={label}>Rate ceiling</label><input className={field} value={form.max_rate} onChange={set('max_rate')} placeholder="$20.00" /></div>
            </div>
            <div>
                <label className={label}>Must haves (comma separated)</label>
                <input className={field} value={form.must_haves} onChange={set('must_haves')}
                       placeholder="ground floor, plumbing for shampoo bowls, parking" />
            </div>
            <div>
                <label className={label}>Notes</label>
                <textarea rows={2} className={field} value={form.notes} onChange={set('notes')} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                    <label className={label}>Sweep</label>
                    <select className={field} value={form.sweep_frequency} onChange={set('sweep_frequency')}>
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="biweekly">Every other week</option>
                        <option value="monthly">Monthly</option>
                        <option value="manual">Manual only</option>
                    </select>
                </div>
                <label className="mt-6 flex items-center gap-2 text-sm text-gray-700">
                    <input type="checkbox" checked={form.rep_agreement_on_file} onChange={set('rep_agreement_on_file')} />
                    Representation agreement on file
                </label>
            </div>
            <div className="flex justify-end gap-2 pt-1">
                <button type="button" onClick={onCancel} className="rounded border border-gray-300 px-4 py-2 text-sm">Cancel</button>
                <button type="submit" disabled={saving} className="rounded bg-slate-800 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
                    {saving ? 'Starting…' : 'Start search'}
                </button>
            </div>
        </form>
    );
};

// One candidate's write actions (status, share, zoning), with its own busy flag, for the
// cells of the candidates list.
const useCandidateActions = (c, onChange) => {
    const [busy, setBusy] = useState(false);
    const apiClient = useApiClient();

    const patch = async (body) => {
        setBusy(true);
        try {
            await apiClient.put(`/api/site-searches/${c.search_id}/candidates/${c.candidate_id}`, body);
            onChange();
        } catch (err) {
            alert(err.message || 'Could not update.');
        } finally {
            setBusy(false);
        }
    };

    // Re-check zoning (online map, or the office-AI queue) / confirm an AI-read district.
    const zoning = async (action) => {
        setBusy(true);
        try {
            const r = await apiClient.post(`/api/site-searches/${c.search_id}/candidates/${c.candidate_id}/${action}`, {});
            if (r?.queued_message) alert(r.queued_message);
            else if (action === 'zoning-recheck' && !r?.lookup?.district) alert(r?.lookup?.reason || 'No district found.');
            onChange();
        } catch (err) {
            alert(err.message || 'Could not check the zoning.');
        } finally {
            setBusy(false);
        }
    };
    return { busy, patch, zoning };
};

// Owner, 5 Oct 2026: the address opens the listing page it was found on in a new tab.
// Owner, 25 Oct 2026: a CommercialCafe building page is labelled as one (it often shows no
// space), and every address gets "Look it up on:" LoopNet, Crexi, CityFeet, CommercialCafe
// (or the home sites for a residential search). Built in utils/listingLinks.js.
const AddressCell = ({ c, ctx = {} }) => (
    <>
        <p className="font-semibold text-gray-900">
            {isFresh(c) && (
                <span className="mr-1 rounded bg-yellow-300 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-yellow-900">
                    New
                </span>
            )}
            <AddressLink c={c} />
        </p>
        <p className="text-xs text-gray-500">{c.municipality}{c.pin ? ` · PIN ${c.pin}` : ''}</p>
        <LinkNote c={c} />
        {Array.isArray(c.sources_seen) && c.sources_seen.length > 1 && (
            <p className="text-[10px] uppercase tracking-wide text-gray-400">seen on {c.sources_seen.map((x) => SOURCE_NAME[x] || x).join(', ')}</p>
        )}
        <LookupLinks c={c} uses={ctx.uses} intent={ctx.intent} />
    </>
);

const ZoneCell = ({ c, onChange }) => {
    const { busy, zoning } = useCandidateActions(c, onChange);
    return (
        <>
            <span className="whitespace-nowrap rounded bg-gray-100 px-2 py-0.5 font-mono text-[11px]">{c.zoning_district || '—'}</span>
            {c.zoning_district && c.zoning_district_source === 'ai_read' && !Number(c.zoning_confirmed) && (
                <span className="mt-1 block text-[10px] font-semibold text-amber-700" title={c.zoning_note || ''}>
                    {c.zoning_note || 'AI-read — confirm'}
                    <button disabled={busy} onClick={() => zoning('zoning-confirm')}
                            className="ml-1 rounded bg-amber-600 px-1.5 py-0.5 text-white disabled:opacity-50">Confirm</button>
                </span>
            )}
            {!c.zoning_district && c.zoning_check?.status === 'queued' && (
                <span className="mt-1 block text-[10px] text-indigo-700">Queued for zoning check</span>
            )}
            {!c.zoning_district && c.zoning_check?.status === 'failed' && (
                <span className="mt-1 block text-[10px] text-red-700" title={c.zoning_check.notes || ''}>AI could not read the map</span>
            )}
            {!c.zoning_district && c.zoning_check?.status !== 'queued' && (
                <button disabled={busy} onClick={() => zoning('zoning-recheck')}
                        className="mt-1 block whitespace-nowrap rounded border border-gray-300 px-1.5 py-0.5 text-[10px] text-gray-700 disabled:opacity-50">
                    {busy ? 'Checking…' : 'Re-check zoning'}
                </button>
            )}
        </>
    );
};

const StatusCell = ({ c, onChange }) => {
    const { busy, patch } = useCandidateActions(c, onChange);
    return (
        <select disabled={busy} value={c.status} onChange={(e) => patch({ status: e.target.value })}
                className={`rounded px-2 py-1 text-xs ${STATUS_TONE[c.status]}`}>
            {CAND_STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s] || s}</option>)}
        </select>
    );
};

const ShareCell = ({ c, onChange }) => {
    const { busy, patch } = useCandidateActions(c, onChange);
    return (
        <label className="flex items-center gap-1 text-xs text-gray-700">
            <input type="checkbox" disabled={busy} checked={!!c.released_to_client}
                   onChange={(e) => patch({ released_to_client: e.target.checked })} />
            {c.released_to_client ? 'Shared' : 'Share'}
        </label>
    );
};

// The candidates list's columns. value() is what search, sort and CSV use: numbers where the
// cell shows a range ("4,531–8,362 SF" sorts on 4531), the zoning answer in its logical order.
const candidateColumns = (onChange, ctx) => [
    { key: 'address', label: 'Address', value: (c) => [c.address, c.municipality, c.pin ? `PIN ${c.pin}` : ''].filter(Boolean).join(' · '), render: (c) => <AddressCell c={c} ctx={ctx} /> },
    { key: 'zoning_district', label: 'Zone', render: (c) => <ZoneCell c={c} onChange={onChange} /> },
    { key: 'use_status', label: 'Use', type: 'number', value: (c) => USE_LABEL[c.use_status] || '', sortValue: (c) => ['by_right', 'special_use', 'unverified', 'not_allowed'].indexOf(c.use_status),
        render: (c) => (Array.isArray(c.uses) && c.uses.length > 1
            // Several uses (site_search_uses): one answer per use, the main use first.
            ? <span className="flex flex-col gap-0.5">{c.uses.map((u) => (
                <Tag key={u.use_category} tone={USE_TONE[u.use_status]}><span title={u.reason || ''}>{u.use_category}: {USE_LABEL[u.use_status]}</span></Tag>))}</span>
            : <Tag tone={USE_TONE[c.use_status]}>{USE_LABEL[c.use_status]}</Tag>) },
    { key: 'size', label: 'SF', type: 'number', value: (c) => c.size_sf || '', sortValue: (c) => c.size_sf_min ?? null, render: (c) => <span className="font-mono text-[11px]">{c.size_sf || '—'}</span> },
    { key: 'rate', label: 'Rate', type: 'number', value: (c) => c.rate || '', sortValue: (c) => { const m = String(c.rate || '').match(/[\d.]+/); return m ? parseFloat(m[0]) : null; },
        render: (c) => <span className="font-mono text-[11px]">{c.rate || '—'}</span> },
    { key: 'listing_agent', label: 'Listing agent', value: (c) => [c.listing_agent, c.listing_brokerage].filter(Boolean).join(' · '),
        render: (c) => <span className="text-xs">{c.listing_agent || '—'}{c.listing_brokerage && <span className="block text-gray-500">{c.listing_brokerage}</span>}</span> },
    { key: 'agent_phone', label: 'Phone', sortable: false,
        render: (c) => <span className="font-mono text-[11px] whitespace-nowrap">{c.agent_phone ? <a className="text-blue-700 hover:underline" href={`tel:${c.agent_phone.replace(/[^\d+]/g, '')}`}>{c.agent_phone}</a> : '—'}</span> },
    { key: 'notes', label: 'Notes', sortable: false, render: (c) => <span className="text-xs text-gray-600">{c.notes}</span> },
    { key: 'status', label: 'Status', type: 'number', value: (c) => STATUS_LABEL[c.status] || c.status, sortValue: (c) => CAND_STATUSES.indexOf(c.status), render: (c) => <StatusCell c={c} onChange={onChange} /> },
    { key: 'released_to_client', label: 'Client', sortable: false, search: false, value: (c) => (c.released_to_client ? 'Shared' : ''), render: (c) => <ShareCell c={c} onChange={onChange} /> },
];

// The uses a search looks for (site_search_uses). The main use is the search's use category;
// extra ones (owner, 22 Oct 2026: colleges & universities in every search) are added here and
// every property already found is checked for them against the Zoning Library.
const UsesEditor = ({ searchId, uses, primary, onChange }) => {
    const apiClient = useApiClient();
    const [busy, setBusy] = useState(false);
    const add = async (use) => {
        const u = (use || '').trim();
        if (!u) return;
        setBusy(true);
        try { const r = await apiClient.post(`/api/site-searches/${searchId}/uses`, { use_category: u }); alert(r.message); onChange(); }
        catch (err) { alert(err.message || 'Could not add the use.'); }
        finally { setBusy(false); }
    };
    const remove = async (use) => {
        if (!window.confirm(`Stop looking for "${use}" in this search?`)) return;
        setBusy(true);
        try { await apiClient.delete(`/api/site-searches/${searchId}/uses?use_category=${encodeURIComponent(use)}`); onChange(); }
        catch (err) { alert(err.message || 'Could not remove the use.'); }
        finally { setBusy(false); }
    };
    return (
        <div className="rounded border border-gray-200 bg-white p-3 text-sm">
            <h4 className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-500">Uses this search looks for</h4>
            <div className="flex flex-wrap items-center gap-1">
                {uses.map((u) => (
                    <span key={u} className={`rounded-full border px-2 py-0.5 text-xs ${u === primary ? 'border-slate-800 bg-slate-800 text-white' : 'border-gray-300 text-gray-700'}`}>
                        {u}{u === primary ? ' (main)' : (
                            <button type="button" disabled={busy} onClick={() => remove(u)} className="ml-1 text-gray-400 hover:text-red-600" aria-label={`Remove ${u}`}>×</button>)}
                    </span>
                ))}
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
                <div className="min-w-[16rem] flex-1">
                    <BusinessPicker value="" exclude={uses} compact onChange={(v) => !busy && add(v)} placeholder="Add a use — type any word (daycare, college…)" />
                </div>
            </div>
        </div>
    );
};

const AddCandidateForm = ({ searchId, onDone }) => {
    const [f, setF] = useState({ address: '', municipality: '', zoning_district: '', use_status: 'unverified',
        size_sf: '', rate: '', listing_agent: '', listing_brokerage: '', agent_phone: '', source: 'loopnet', notes: '' });
    const [saving, setSaving] = useState(false);
    const apiClient = useApiClient();
    const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
    const field = 'mt-1 w-full rounded border border-gray-300 px-2 py-1.5 text-sm';

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await apiClient.post(`/api/site-searches/${searchId}/candidates`, f);
            onDone();
        } catch (err) {
            alert(err.message || 'Could not add the property.');
        } finally { setSaving(false); }
    };

    return (
        <form onSubmit={submit} className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><label className="text-xs font-semibold text-gray-600">Address</label>
                <input required className={field} value={f.address} onChange={set('address')} placeholder="1945 Techny Rd" /></div>
            <div><label className="text-xs font-semibold text-gray-600">Town</label><input className={field} value={f.municipality} onChange={set('municipality')} /></div>
            <div><label className="text-xs font-semibold text-gray-600">Zoning district</label><input className={field} value={f.zoning_district} onChange={set('zoning_district')} placeholder="I-1" /></div>
            <div><label className="text-xs font-semibold text-gray-600">Use status</label>
                <select className={field} value={f.use_status} onChange={set('use_status')}>
                    {Object.keys(USE_LABEL).map((k) => <option key={k} value={k}>{USE_LABEL[k]}</option>)}
                </select></div>
            <div><label className="text-xs font-semibold text-gray-600">Size SF</label><input className={field} value={f.size_sf} onChange={set('size_sf')} placeholder="4,531–8,362" /></div>
            <div><label className="text-xs font-semibold text-gray-600">Rate</label><input className={field} value={f.rate} onChange={set('rate')} placeholder="$11.00" /></div>
            <div><label className="text-xs font-semibold text-gray-600">Source</label>
                <select className={field} value={f.source} onChange={set('source')}>
                    <option value="loopnet">LoopNet</option><option value="crexi">Crexi</option>
                    <option value="cityfeet">CityFeet</option><option value="commercialcafe">CommercialCafe</option>
                    <option value="commercialsearch">CommercialSearch</option><option value="zillow">Zillow</option>
                    <option value="realtor">Realtor.com</option><option value="apartments">Apartments.com</option><option value="forrent">ForRent</option><option value="other">Other</option>
                </select></div>
            <div><label className="text-xs font-semibold text-gray-600">Listing agent</label><input className={field} value={f.listing_agent} onChange={set('listing_agent')} /></div>
            <div><label className="text-xs font-semibold text-gray-600">Brokerage</label><input className={field} value={f.listing_brokerage} onChange={set('listing_brokerage')} /></div>
            <div><label className="text-xs font-semibold text-gray-600">Agent phone</label><input className={field} value={f.agent_phone} onChange={set('agent_phone')} /></div>
            <div className="col-span-2"><label className="text-xs font-semibold text-gray-600">Notes</label><input className={field} value={f.notes} onChange={set('notes')} /></div>
            <div className="col-span-2 flex justify-end">
                <button type="submit" disabled={saving} className="rounded bg-slate-800 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
                    {saving ? 'Adding…' : 'Add property'}
                </button>
            </div>
        </form>
    );
};

// Is the office computer that runs searches checking in? Shown beside "Run search now",
// so nobody presses it and waits on a computer that is switched off. Full detail:
// Administration → System status.
const HEALTH_DOT = { green: 'bg-green-500', amber: 'bg-amber-500', red: 'bg-red-500' };
const RunnerHealth = () => {
    const [h, setH] = useState(null);
    const apiClient = useApiClient();
    useEffect(() => {
        let live = true;
        apiClient.get('/api/ops-status/runner-health').then((r) => { if (live) setH(r); }).catch(() => {});
        return () => { live = false; };
    }, [apiClient]);
    if (!h) return null;
    return (
        <span className="flex items-center gap-1 text-xs text-gray-600" title={`${h.headline} ${h.advice}`}>
            <span className={`inline-block h-2.5 w-2.5 rounded-full ${HEALTH_DOT[h.tone] || 'bg-gray-400'}`} />
            {h.short}
        </span>
    );
};

const SearchDetail = ({ searchId, onBack, onChanged }) => {
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [adding, setAdding] = useState(false);
    const [pasting, setPasting] = useState(false);
    const [clipsWaiting, setClipsWaiting] = useState(0);
    const [closing, setClosing] = useState(false);
    const [closeReason, setCloseReason] = useState('leased');
    const [fTown, setFTown] = useState('');
    const [fUse, setFUse] = useState('');
    const [fStatus, setFStatus] = useState('');
    const [fMinSf, setFMinSf] = useState('');
    const [fMaxSf, setFMaxSf] = useState('');
    const [showParked, setShowParked] = useState(false);
    const [freshOnly, setFreshOnly] = useState(false);
    const apiClient = useApiClient();
    // Owner, 5 Oct 2026: opening a search must land on its top (title, filters), not at the
    // bottom of a long property list; scroll down from there yourself.
    const topRef = useRef(null);
    const scrolled = useRef(false);
    useEffect(() => {
        if (!data || scrolled.current || !topRef.current) return;
        scrolled.current = true;
        topRef.current.scrollIntoView({ block: 'start' });
    }, [data]);

    const load = useCallback(async () => {
        try { setData(await apiClient.get(`/api/site-searches/${searchId}`)); }
        catch (err) { setError(err.message || 'Could not load the search.'); }
    }, [apiClient, searchId]);
    useEffect(() => { load(); }, [load]);
    // Pages sent with the Send to BREC bookmarklet and not yet filed.
    useEffect(() => {
        apiClient.get('/api/site-searches/clips').then((r) => setClipsWaiting(Array.isArray(r) ? r.length : 0)).catch(() => {});
    }, [apiClient]);

    const runNow = async () => {
        try { const r = await apiClient.post(`/api/site-searches/${searchId}/run-now`, {}); alert(r.message); load(); }
        catch (err) { alert(err.message); }
    };
    const setFrequency = async (sweep_frequency) => {
        try { await apiClient.put(`/api/site-searches/${searchId}`, { sweep_frequency }); load(); }
        catch (err) { alert(err.message); }
    };

    const setStatus = async (status, reason) => {
        try {
            await apiClient.put(`/api/site-searches/${searchId}`, { status, closed_reason: reason });
            setClosing(false); load(); onChanged();
        } catch (err) { alert(err.message); }
    };

    if (error) return <p className="rounded bg-red-50 p-3 text-sm text-red-700">{error}</p>;
    if (!data) return <p className="p-3 text-sm text-gray-500">Loading…</p>;

    const { search, candidates, jurisdictions, activity } = data;
    const crit = search.criteria || {};
    const byRight = candidates.filter((c) => c.use_status === 'by_right').length;
    const towns = [...new Set(candidates.map((c) => c.municipality).filter(Boolean))].sort();
    const freshCount = candidates.filter(isFresh).length;

    const minSf = fMinSf ? Number(String(fMinSf).replace(/\D/g, '')) : null;
    const maxSf = fMaxSf ? Number(String(fMaxSf).replace(/\D/g, '')) : null;
    // These filters run first; the list's own search box, header sort and CSV work on what is left.
    const shown = candidates.filter((c) => {
        if (freshOnly && !isFresh(c)) return false;
        if (!showParked && PARKED.includes(c.status) && fStatus !== c.status) return false;
        if (fTown && c.municipality !== fTown) return false;
        if (fUse && c.use_status !== fUse) return false;
        if (fStatus && c.status !== fStatus) return false;
        // A range overlaps the filter if it is not entirely above or below it.
        if (minSf && c.size_sf_max != null && c.size_sf_max < minSf) return false;
        if (maxSf && c.size_sf_min != null && c.size_sf_min > maxSf) return false;
        return true;
    });

    return (
        <div ref={topRef} className="space-y-4 scroll-mt-4">
            <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                    <button onClick={onBack} className="text-xs text-indigo-600 hover:underline">← All searches</button>
                    <h3 className="text-lg font-bold text-gray-900">{search.title}</h3>
                    <p className="text-sm text-gray-600">
                        {search.use_category} · {search.intent} · {candidates.length} properties, {byRight} by right
                        {search.last_swept_at && <> · last run {new Date(search.last_swept_at).toLocaleString()}</>}
                    </p>
                    {search.last_sweep_summary && <p className="text-xs text-gray-500">Last run: {search.last_sweep_summary}</p>}
                    {/* Saved-search alerts on LoopNet / Crexi are filed here by this name (Alert emails). */}
                    <p className="text-xs text-gray-500">
                        Alert name for LoopNet / Crexi: <span className="select-all font-mono text-gray-700">BREC-{search.search_id} {search.title}</span>
                        {' '}· <a href="/dashboard/site-search-alerts" className="text-indigo-600 hover:underline">Alert emails</a>
                    </p>
                    {search.sweep_requested_at && (
                        <p className="text-xs text-indigo-700">Run requested {new Date(search.sweep_requested_at).toLocaleString()}: it starts at the next check (about every 30 minutes, while the office computer is on).</p>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <span className={`rounded px-2 py-1 text-xs font-semibold ${
                        search.status === 'active' ? 'bg-green-100 text-green-800'
                            : search.status === 'paused' ? 'bg-amber-100 text-amber-800' : 'bg-gray-200 text-gray-700'}`}>
                        {search.status}{search.closed_reason ? ` · ${search.closed_reason}` : ''}
                    </span>
                    {search.status === 'active' && (
                        <>
                            <select value={search.sweep_frequency} onChange={(e) => setFrequency(e.target.value)}
                                    title="How often the search runs by itself" className="rounded border border-gray-300 px-2 py-1 text-xs">
                                <option value="daily">Runs daily</option>
                                <option value="weekly">Runs weekly</option>
                                <option value="biweekly">Every other week</option>
                                <option value="monthly">Monthly</option>
                                <option value="manual">Only when I press Run</option>
                            </select>
                            <button onClick={runNow} disabled={!!search.sweep_requested_at}
                                    className="rounded bg-slate-800 px-3 py-1 text-xs font-semibold text-white disabled:opacity-50">
                                {search.sweep_requested_at ? 'Run queued…' : 'Run search now'}
                            </button>
                            <RunnerHealth />
                            <button onClick={() => setStatus('paused')} className="rounded border border-gray-300 px-3 py-1 text-xs">Pause sweeps</button>
                            <button onClick={() => setClosing(true)} className="rounded border border-gray-300 px-3 py-1 text-xs">Close search</button>
                        </>
                    )}
                    {search.status === 'paused' && (
                        <button onClick={() => setStatus('active')} className="rounded border border-gray-300 px-3 py-1 text-xs">Resume</button>
                    )}
                </div>
            </div>

            {!search.rep_agreement_on_file && (
                <p className="rounded border-l-4 border-amber-500 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                    No representation agreement recorded for this search. Searching for space for compensation is
                    brokerage — put the written agreement on file.
                </p>
            )}

            <div className="rounded border border-gray-200 bg-white p-3 text-sm">
                <h4 className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-500">Search parameters</h4>
                <p className="text-gray-700">
                    <strong>Towns:</strong> {(crit.towns || []).join(', ') || '—'} ·{' '}
                    <strong>Size:</strong> {crit.min_sf || '—'}–{crit.max_sf || '—'} SF ·{' '}
                    <strong>Rate ceiling:</strong> {crit.max_rate || '—'}
                </p>
                {!!(crit.must_haves || []).length && <p className="text-gray-700"><strong>Must have:</strong> {crit.must_haves.join(', ')}</p>}
                {crit.notes && <p className="text-gray-600">{crit.notes}</p>}
            </div>

            <UsesEditor searchId={searchId} uses={search.uses || [search.use_category]} primary={search.use_category} onChange={load} />
            <NotResearched gaps={data.not_researched || []} origin="client_search" searchId={searchId} onDone={load} />

            {!!jurisdictions.length && (
                <div className="grid gap-2 md:grid-cols-2">
                    {jurisdictions.map((j) => (
                        <div key={j.jurisdiction_id || `${j.municipality}-${j.use_category}`} className={`rounded border-l-4 p-2 text-xs ${
                            j.verdict_status === 'by_right' ? 'border-green-500 bg-green-50'
                                : j.verdict_status === 'special_use' ? 'border-amber-500 bg-amber-50'
                                : j.verdict_status === 'not_allowed' ? 'border-red-500 bg-red-50' : 'border-gray-400 bg-gray-50'}`}>
                            <p className="font-semibold text-gray-900">{j.municipality}
                                {(search.uses || []).length > 1 && <span className="ml-1 font-normal text-gray-500">· {j.use_category}</span>}
                                {j.districts_by_right && <span className="ml-1 font-normal text-gray-600">({j.districts_by_right})</span>}</p>
                            <p className="text-gray-700">{j.verdict}</p>
                            {j.conditions && <p className="text-gray-600">Conditions: {j.conditions}</p>}
                            <p className="text-gray-600">
                                {j.official_name ? `${j.official_name}${j.official_title ? `, ${j.official_title}` : ''} · ` : ''}
                                {j.official_phone || j.department_phone || ''}
                            </p>
                        </div>
                    ))}
                </div>
            )}

            <details className="rounded border border-gray-200 bg-white p-3">
                <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wide text-gray-500">Look up an address (zoning for {search.use_category})</summary>
                <div className="mt-2">
                    <ZoningLookupBox useCategory={search.use_category} searchId={searchId} onAdded={load} />
                </div>
            </details>

            <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-gray-800">
                    Properties <span className="font-normal text-gray-500">— {shown.length} of {candidates.length}</span>
                </h4>
                <div className="flex gap-2">
                    <button onClick={() => setPasting(true)} className="rounded bg-slate-800 px-3 py-1.5 text-xs font-semibold text-white"
                            title="Paste a listing page (LoopNet, Crexi, CityFeet…) or use the Send to BREC bookmarklet">Add a listing</button>
                    <button onClick={() => setAdding(true)} className="rounded bg-gray-800 px-3 py-1.5 text-xs font-semibold text-white">Add property</button>
                </div>
            </div>
            {clipsWaiting > 0 && (
                <p className="rounded bg-indigo-50 px-3 py-2 text-xs text-indigo-900">
                    {clipsWaiting} page{clipsWaiting === 1 ? '' : 's'} sent with Send to BREC waiting to be filed —{' '}
                    <a className="font-semibold underline" href="/dashboard/site-search-clip">open</a>.
                </p>
            )}

            <div className="flex flex-wrap items-center gap-2 rounded border border-gray-200 bg-white p-2 text-xs">
                <select value={fTown} onChange={(e) => setFTown(e.target.value)} className="rounded border border-gray-300 px-2 py-1">
                    <option value="">All towns</option>
                    {towns.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <select value={fUse} onChange={(e) => setFUse(e.target.value)} className="rounded border border-gray-300 px-2 py-1">
                    <option value="">Any zoning answer</option>
                    {Object.keys(USE_LABEL).map((k) => <option key={k} value={k}>{USE_LABEL[k]}</option>)}
                </select>
                <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} className="rounded border border-gray-300 px-2 py-1">
                    <option value="">Any status</option>
                    {CAND_STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s] || s}</option>)}
                </select>
                <input value={fMinSf} onChange={(e) => setFMinSf(e.target.value)} placeholder="Min SF"
                       title="Sizes as listed are often wrong, and suites can be combined or divided — use this to look, not to rule properties out."
                       className="w-20 rounded border border-gray-300 px-2 py-1" />
                <input value={fMaxSf} onChange={(e) => setFMaxSf(e.target.value)} placeholder="Max SF"
                       title="Sizes as listed are often wrong, and suites can be combined or divided — use this to look, not to rule properties out."
                       className="w-20 rounded border border-gray-300 px-2 py-1" />
                <label className="flex items-center gap-1 font-semibold text-yellow-800">
                    <input type="checkbox" checked={freshOnly} onChange={(e) => setFreshOnly(e.target.checked)} />
                    New only{freshCount ? ` (${freshCount})` : ''}
                </label>
                <label className="flex items-center gap-1 text-gray-700">
                    <input type="checkbox" checked={showParked} onChange={(e) => setShowParked(e.target.checked)} />
                    Show rejected &amp; off market
                </label>
                <button type="button"
                        onClick={() => { setFTown(''); setFUse(''); setFStatus(''); setFMinSf(''); setFMaxSf(''); setShowParked(false); setFreshOnly(false); }}
                        className="ml-auto rounded border border-gray-300 px-2 py-1 text-gray-600">Clear</button>
                {(fMinSf || fMaxSf) && (
                    <p className="w-full text-[11px] text-amber-700">
                        Size filter on — listed sizes are often wrong and suites combine or divide, so this
                        hides properties that may still work. The sweep files every size; only this view is filtered.
                    </p>
                )}
            </div>

            <div className="rounded border border-gray-200 bg-white p-2">
                {/* Whatever the chosen sort, a new find sits above an old one: the daily
                    sweep exists so that today's arrival is seen today. */}
                <DataList rows={shown} columns={candidateColumns(load, { uses: search.uses || [search.use_category], intent: search.intent })} rowKey="candidate_id" csvName="site-search-candidates"
                    defaultSort={{ key: 'use_status', dir: 'asc' }} pinTop={isFresh}
                    rowClass={(c) => (isFresh(c) ? 'bg-yellow-50' : (PARKED.includes(c.status) || c.use_status === 'not_allowed') ? 'bg-gray-50 text-gray-500' : '')}
                    empty={candidates.length ? 'Nothing matches those filters.' : 'Nothing found yet. Add a property, or wait for the next sweep.'} />
            </div>

            {!!activity.length && (
                <details className="rounded border border-gray-200 bg-white p-3">
                    <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wide text-gray-500">History</summary>
                    <ul className="mt-2 space-y-1 text-xs text-gray-600">
                        {activity.map((a) => (
                            <li key={a.activity_id}>
                                <span className="font-mono text-[11px]">{new Date(a.created_at).toLocaleString()}</span>
                                {' · '}<span className="font-semibold">{a.event}</span>
                                {a.detail ? ` · ${a.detail}` : ''}{a.actor ? ` · ${a.actor}` : ''}
                            </li>
                        ))}
                    </ul>
                </details>
            )}

            <Modal open={adding} onClose={() => setAdding(false)} title="Add a property">
                <AddCandidateForm searchId={searchId} onDone={() => { setAdding(false); load(); }} />
            </Modal>

            <Modal open={pasting} onClose={() => setPasting(false)} title="Add a listing" size="lg">
                <AddListingPaste searchId={searchId} onDone={() => load()} onCancel={() => setPasting(false)} />
            </Modal>

            <Modal open={closing} onClose={() => setClosing(false)} title="Close this search">
                <div className="space-y-3">
                    <p className="text-sm text-gray-700">Closing stops the automated sweeps. Why is it ending?</p>
                    <select value={closeReason} onChange={(e) => setCloseReason(e.target.value)}
                            className="w-full rounded border border-gray-300 px-3 py-2 text-sm">
                        {CLOSE_REASONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                    <div className="flex justify-end gap-2">
                        <button onClick={() => setClosing(false)} className="rounded border border-gray-300 px-4 py-2 text-sm">Cancel</button>
                        <button onClick={() => setStatus('closed', closeReason)} className="rounded bg-gray-900 px-4 py-2 text-sm font-semibold text-white">Close search</button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

const SiteSearchPanel = ({ clientId }) => {
    const [searches, setSearches] = useState([]);
    const [error, setError] = useState('');
    const [openId, setOpenId] = useState(null);
    const [creating, setCreating] = useState(false);
    const apiClient = useApiClient();

    const load = useCallback(async () => {
        try { setSearches(await apiClient.get(`/api/site-searches?client_id=${clientId}`)); }
        catch (err) { setError(err.message || 'Could not load searches.'); }
    }, [apiClient, clientId]);
    useEffect(() => { load(); }, [load]);


    if (openId) return <SearchDetail searchId={openId} onBack={() => { setOpenId(null); load(); }} onChanged={load} />;

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-lg font-bold text-gray-900">Site search</h3>
                    <p className="text-sm text-gray-600">Space we are looking for on this client's behalf.</p>
                </div>
                <button onClick={() => setCreating(true)} className="rounded bg-slate-800 px-3 py-1.5 text-sm font-semibold text-white">
                    Start a search
                </button>
            </div>
            {error && <p className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

            {/* Above the searches, because it is the answer whether or not a search
                exists yet: the shared zoning library already knows most of it. */}
            <ZoningBrief clientId={clientId} onSearchStarted={load} />

            {!!searches.length && (
                <div className="rounded border border-gray-200 bg-white p-2">
                    <DataList rows={searches} rowKey="search_id" csvName="site-searches" onRowClick={(s) => setOpenId(s.search_id)}
                        defaultSort={{ key: 'last_swept_at', dir: 'desc' }} pinTop={(s) => s.status !== 'closed'}
                        filters={[{ key: 'status', label: 'Status' }, { key: 'use_category', label: 'Use' }]}
                        columns={[
                            { key: 'title', label: 'Search', render: (s) => <span className="font-semibold text-gray-900">{s.title}</span> },
                            { key: 'status', label: 'Status', value: (s) => `${s.status}${s.closed_reason ? ` · ${s.closed_reason}` : ''}`,
                                render: (s) => <span className={`rounded px-2 py-0.5 text-xs font-semibold ${
                                    s.status === 'active' ? 'bg-green-100 text-green-800'
                                        : s.status === 'paused' ? 'bg-amber-100 text-amber-800' : 'bg-gray-200 text-gray-700'}`}>
                                    {s.status}{s.closed_reason ? ` · ${s.closed_reason}` : ''}</span> },
                            { key: 'use_category', label: 'Use' },
                            { key: 'candidate_count', label: 'Properties', type: 'number' },
                            { key: 'new_count', label: 'New', type: 'number',
                                render: (s) => (s.new_count > 0 ? <span className="rounded bg-yellow-300 px-1.5 py-0.5 text-xs font-bold text-yellow-900">{s.new_count} new</span> : <span className="text-xs text-gray-500">none new</span>) },
                            { key: 'released_count', label: 'Shared with client', type: 'number' },
                            { key: 'last_swept_at', label: 'Swept', type: 'date' },
                        ]} />
                </div>
            )}
            {!searches.length && !error && (
                <p className="rounded border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
                    No search yet for this client.
                </p>
            )}

            <Modal open={creating} onClose={() => setCreating(false)} title="Start a site search">
                <NewSearchForm clientId={clientId} onDone={() => { setCreating(false); load(); }} onCancel={() => setCreating(false)} />
            </Modal>
        </div>
    );
};

export default SiteSearchPanel;
